import React, { useState, useEffect, useMemo, useCallback } from "react";
import "./Tree.scss";
import TreeNode, { type CheckedNode } from "./TreeNode/TreeNode";
import {
  search,
  TristateStateEnum,
  getNodeById,
  isSearchTermActive,
  countNodes,
  MIN_SEARCH_LENGTH,
  MIN_SEARCH_LENGTH_HINT,
  MIN_SEARCH_LENGTH_NODE_THRESHOLD,
  SEARCH_DEBOUNCE_MS,
  updateChildrenWithParentId,
  type TristateState,
} from "./TreeUtils";
import { useDebounce } from "react-use";
import {
  FormFieldError,
  Loader,
  SkeletonLoader,
  TableLoader,
  TextInput,
} from "@SeemplicityDev/shared";
import ClassNames from "classnames";
import type { TreeActionTooltips, TreeNode as TreeNodeData } from "./types";

interface CheckedTreeNode extends TreeNodeData {
  checked?: TristateState;
}

interface TreeNodeOperations {
  onEdit?: (node: TreeNodeData) => void;
  // Opt-in third row action, rendered between Edit and Delete.
  onMove?: (node: TreeNodeData) => void;
  onSelect?: (node: TreeNodeData) => void;
  onDelete?: (node: TreeNodeData) => void;
  onCheck?: (node: TreeNodeData, checkedNodes?: CheckedNode[]) => void;
  onWarning?: (id?: string) => boolean;
}

interface TreeError {
  message?: string;
  type?: string;
}

export interface TreeProps {
  data?: TreeNodeData[] | null;
  nodeOperations?: TreeNodeOperations;
  isSearchable?: boolean;
  required?: boolean;
  hideLabelHelper?: boolean;
  "data-testid"?: string;
  editableItems?: boolean;
  selectedNode?: string | null;
  withCheckbox?: boolean;
  error?: TreeError | null;
  tristate?: boolean;
  onChange?: (checkedNodes: CheckedTreeNode[]) => void;
  initialExpandLevel?: number;
  initialExpandedNodes?: string[];
  placeholder?: string;
  preCheckedNodes?: TreeNodeData[];
  label?: string;
  className?: string;
  isSelectableNode?: (node: TreeNodeData) => boolean;
  isHiddenRoot?: boolean;
  readOnly?: boolean;
  warningTooltip?: React.ReactNode;
  getActionTooltips?: (node: TreeNodeData) => TreeActionTooltips;
  borderlessNodes?: boolean;
  previewMode?: boolean;
  isSmall?: boolean;
  scrollToSelected?: boolean;
  getNodeIconByType?: ((type?: string, id?: string) => React.ReactNode) | null;
  getNodeActions?: ((node: TreeNodeData) => React.ReactNode) | null;
  getRightComponent?: ((node: TreeNodeData) => React.ReactNode) | null;
  getExtendedTitle?: ((node: TreeNodeData) => React.ReactNode) | null;
  loadingRows?: number;
  tooltipContent?: ((node: TreeNodeData) => React.ReactNode) | null;
  // Fully expanded, no expand toggles — for read-only hierarchy views.
  isStatic?: boolean;
  isHighlightedNode?: ((node: TreeNodeData) => boolean) | null;
}

const Tree: React.FC<TreeProps> = ({
  data = null,
  nodeOperations = {},
  isSearchable,
  required,
  hideLabelHelper = true,
  "data-testid": dataTestId,
  editableItems = false,
  selectedNode = null,
  withCheckbox = false,
  error = null,
  tristate = false,
  onChange = () => {},
  initialExpandLevel = 1,
  initialExpandedNodes,
  placeholder = "",
  preCheckedNodes = [],
  label = "",
  className = "",
  isSelectableNode = () => false,
  isHiddenRoot = false,
  readOnly = false,
  warningTooltip,
  getActionTooltips,
  borderlessNodes = false,
  previewMode = false,
  isSmall = false,
  scrollToSelected = false,
  getNodeIconByType = null,
  getNodeActions = null,
  getRightComponent = null,
  getExtendedTitle = null,
  loadingRows = 9,
  tooltipContent = null,
  isStatic = false,
  isHighlightedNode = null,
}) => {
  const [searchValue, setSearchValue] = useState("");
  // Term that produced the current `treeData` - empty below the minimum length.
  const [effectiveSearchValue, setEffectiveSearchValue] = useState("");
  const [treeData, setTreeData] = useState<TreeNodeData[] | null>(null);
  const [manipulatedData, setManipulatedData] = useState<TreeNodeData[] | null>(
    null
  );
  const [isSearching, setIsSearching] = useState(false);

  const [checkedNodes, setCheckedNodes] = useState<CheckedTreeNode[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<string[]>(
    initialExpandedNodes ?? []
  );

  useEffect(() => {
    if (!!data) {
      const newTree = updateChildrenWithParentId(data, null);
      setManipulatedData(newTree);
    }
  }, [data]);

  // Only large trees are slow enough to justify gating the search.
  const isLargeTree = useMemo(
    () => countNodes(manipulatedData) > MIN_SEARCH_LENGTH_NODE_THRESHOLD,
    [manipulatedData]
  );
  const minSearchLength = isLargeTree ? MIN_SEARCH_LENGTH : 1;

  useEffect(() => {
    if (!!preCheckedNodes?.length && !!treeData) {
      const updatedCheckedNodes = preCheckedNodes.map((node) => {
        return {
          ...node,
          ...getNodeById(treeData[0], node.id ?? ""),
        };
      });

      const checkedIndeterminateList =
        getCheckedAndIndeterminateNodes(updatedCheckedNodes);

      setCheckedNodes(checkedIndeterminateList);
    }
  }, [preCheckedNodes, treeData]);

  useEffect(() => {
    if (!!selectedNode && !!manipulatedData) {
      const selectedNodeBranch = getSelectedNodeBranch(selectedNode, []);
      setExpandedNodes(selectedNodeBranch);
    }
  }, [manipulatedData, selectedNode]);

  const getSelectedNodeBranch = (
    nodeId: string | null | undefined,
    branch: string[]
  ): string[] => {
    if (!nodeId) {
      return branch;
    }

    const node = getNodeById(manipulatedData?.[0], nodeId);
    const parent = node?.parentId;

    if (!!parent) {
      branch.push(parent);
    }

    return getSelectedNodeBranch(parent, branch);
  };

  useDebounce(
    () => {
      const term = isSearchTermActive(searchValue, minSearchLength)
        ? searchValue
        : "";
      setTreeData(term ? search(manipulatedData ?? [], term) : manipulatedData);
      setEffectiveSearchValue(term);
      setIsSearching(false);
    },
    SEARCH_DEBOUNCE_MS,
    [searchValue, manipulatedData, minSearchLength]
  );

  const noDataState = useMemo(
    () => (
      <div className={`tree-nodes-wrap no-data ${isSmall ? "is-small" : ""}`}>
        <Loader
          flexcenter
          largeTitle={!isSmall}
          icon={"seem-icon-no-scope-group-results"}
          className="loader-no-scopes-found no-filter"
          title={"No Scopes or Groups Found"}
          iconSize={"xxxl"}
          subtitle={"Try adjusting your search"}
          fullHeight
        />
      </div>
    ),
    []
  );

  const loadingState = useMemo(
    () => (
      <div className={"tree-nodes-wrap is-loading"}>
        <SkeletonLoader
          className={"tree-loader-title"}
          width={"180px"}
          height={"16px"}
        />
        <table className={"tree-loader"}>
          <TableLoader
            rowHeight={16}
            rows={loadingRows}
            colsWidth={[410]}
            data-testid={"tree-loader"}
          />
        </table>
      </div>
    ),
    []
  );

  const getCheckedAndIndeterminateNodes = useCallback(
    (checkedNodes: CheckedTreeNode[]) => {
      const indeterminateNodesList = new Map<string, CheckedTreeNode>();
      const checkedNodesList = new Map<string, CheckedTreeNode>();

      const setIndeterminateOrCheckedParent = (
        parentId: string | null | undefined
      ) => {
        const parent =
          !!treeData && parentId ? getNodeById(treeData[0], parentId) : null;
        if (!!parent) {
          if (!checkedNodes.find((checked) => checked.id === parent.id)) {
            if (
              !indeterminateNodesList.has(parent.id ?? "") &&
              !checkedNodesList.has(parent.id ?? "")
            ) {
              // if it already exists - we already ran through its ancestors

              const isChecked = parent?.children?.every(
                (
                  child // Check if all the node's children are checked - if so it should be checked as well
                ) => checkedNodesList.has(child.id ?? "")
              );

              if (isChecked) {
                checkedNodesList.set(parent.id ?? "", {
                  ...parent,
                  checked: TristateStateEnum.CHECKED,
                });
              } else {
                indeterminateNodesList.set(parent.id ?? "", {
                  ...parent,
                  checked: TristateStateEnum.INDETERMINATE,
                });
              }
              setIndeterminateOrCheckedParent(parent.parentId);
            }
          }
        }
      };

      const setCheckedChildren = (node: TreeNodeData) => {
        node?.children?.forEach((child) => {
          if (!checkedNodes.find((checked) => checked.id === child.id)) {
            if (!checkedNodesList.has(child.id ?? "")) {
              checkedNodesList.set(child.id ?? "", {
                ...child,
                checked: TristateStateEnum.CHECKED,
              });
              setCheckedChildren(child);
            }
          }
        });
      };

      checkedNodes.forEach((node) => {
        checkedNodesList.set(node.id ?? "", {
          ...node,
          checked: TristateStateEnum.CHECKED,
        });
        if (tristate) {
          setCheckedChildren(node);
          setIndeterminateOrCheckedParent(node.parentId);
        }
      });

      const indeterminateArray = tristate
        ? Array.from(indeterminateNodesList.values())
        : [];
      const checkedArray = Array.from(checkedNodesList.values());
      return Array.from(new Set(checkedArray.concat(indeterminateArray))); // prevent duplication
    },
    [treeData]
  );

  const handleNodeCheck = useCallback(
    (
      newlyCheckedNode: TreeNodeData,
      alreadyCheckedNodes: CheckedNode[] = []
    ) => {
      // `alreadyCheckedNodes` carries the full node data at runtime (the tree's
      // own state holds complete nodes), so it is treated as CheckedTreeNode[].
      const checkedNodesInput = alreadyCheckedNodes as CheckedTreeNode[];
      const setParent = (
        parentId: string | null | undefined,
        currNodes: CheckedTreeNode[]
      ): CheckedTreeNode[] | undefined => {
        if (!parentId) {
          // no parent - return the current checked list - we reached the root (stop recursion)
          return currNodes;
        }

        let nodesCopy = currNodes.slice(); // new instance to checked list
        const parent = !!treeData && getNodeById(treeData[0], parentId); // get the parent from the tree
        if (!!parent) {
          // parent exists in tree
          if (
            parent.children?.every(
              (
                child // all the parent's children are checked (only checked - not indeterminate)
              ) =>
                nodesCopy.find(
                  (checkedNode) =>
                    checkedNode?.id === child.id &&
                    checkedNode.checked === TristateStateEnum.CHECKED
                )
            )
          ) {
            const nodeIndex = nodesCopy.findIndex(
              (checked) => checked.id === parent.id
            );
            if (nodeIndex !== -1) {
              nodesCopy[nodeIndex].checked = TristateStateEnum.CHECKED;
            } else {
              nodesCopy.push({ ...parent, checked: TristateStateEnum.CHECKED }); // add to checked list
            }
          } else if (
            parent.children?.every(
              // all the parent's children are unchecked
              (child) =>
                !nodesCopy.find((checkedNode) => checkedNode?.id === child.id)
            )
          ) {
            const index = nodesCopy.findIndex(
              // remove from checked list
              (checked) => checked.id === parent.id
            );
            if (index !== -1) {
              nodesCopy.splice(index, 1);
            }
          } else {
            // if not all the children are checked and not all the children are unchecked - then the parent is indeterminate
            nodesCopy = setIndeterminate(parent, nodesCopy); // set the parent's state is indeterminate
          }

          nodesCopy = setParent(parent.parentId, nodesCopy) ?? nodesCopy; // do the same for the parent's parent
          return nodesCopy; // return the checked nodes after the parent is the root - which means we went through the whole tree
        }
      };

      const setCheckedChildren = (
        node: TreeNodeData,
        currCheckedNodes: CheckedTreeNode[]
      ): CheckedTreeNode[] => {
        node?.children?.forEach((child) => {
          const childCheckedIndex = currCheckedNodes.findIndex(
            (checkedNode) => checkedNode.id === child.id
          );
          if (childCheckedIndex === -1) {
            currCheckedNodes.push({
              ...child,
              checked: TristateStateEnum.CHECKED,
            });
          } else {
            currCheckedNodes[childCheckedIndex] = {
              ...child,
              checked: TristateStateEnum.CHECKED,
            };
          }

          if (!!child?.children?.length) {
            setCheckedChildren(child, currCheckedNodes);
          }
        });

        return currCheckedNodes;
      };

      const setUncheckedChildren = (
        node: TreeNodeData,
        currCheckedNodes: CheckedTreeNode[]
      ): CheckedTreeNode[] => {
        node?.children?.forEach((child) => {
          const nodeCheckedIndex = currCheckedNodes.findIndex(
            (checkedNode) => checkedNode.id === child.id
          );
          if (nodeCheckedIndex !== -1) {
            currCheckedNodes.splice(nodeCheckedIndex, 1);
          }

          if (!!node?.children?.length) {
            setUncheckedChildren(child, currCheckedNodes);
          }
        });

        return currCheckedNodes;
      };

      const setIndeterminate = (
        node: TreeNodeData,
        currNodes: CheckedTreeNode[]
      ): CheckedTreeNode[] => {
        const nodesCopy = currNodes.slice();
        const nodeCheckedIndex = nodesCopy.findIndex(
          (checkedNode) => checkedNode.id === node.id
        );
        if (nodeCheckedIndex === -1) {
          nodesCopy.push({ ...node, checked: TristateStateEnum.INDETERMINATE });
        } else {
          nodesCopy[nodeCheckedIndex] = {
            ...node,
            checked: TristateStateEnum.INDETERMINATE,
          };
        }

        return nodesCopy;
      };

      let checkedNodesCopy = checkedNodesInput.slice();

      const checkedNodeIndex = checkedNodesCopy.findIndex(
        (checkedNode) =>
          checkedNode.id === newlyCheckedNode.id &&
          checkedNode.checked === TristateStateEnum.CHECKED
      );

      if (
        // the current node is in the checked nodes and its status is checked
        checkedNodeIndex !== -1
      ) {
        checkedNodesCopy.splice(checkedNodeIndex, 1); // uncheck the selected node
        if (tristate) {
          // tristate - uncheck its children
          checkedNodesCopy = setUncheckedChildren(
            // uncheck its children
            newlyCheckedNode,
            checkedNodesCopy.slice()
          );
        }
      } else {
        const index = checkedNodesCopy.findIndex(
          (checkedNode) =>
            checkedNode.id === newlyCheckedNode.id &&
            checkedNode.checked === TristateStateEnum.INDETERMINATE
        );

        if (index !== -1) {
          // check the selected node
          checkedNodesCopy[index].checked = TristateStateEnum.CHECKED;
        } else {
          checkedNodesCopy.push({
            ...newlyCheckedNode,
            checked: TristateStateEnum.CHECKED,
          });
        }
        if (tristate) {
          // tristate - check its children

          checkedNodesCopy = setCheckedChildren(
            // check its children
            newlyCheckedNode,
            checkedNodesCopy.slice()
          );
        }
      }

      if (tristate) {
        // if its tristate - we need to change ancestors state accordingly
        checkedNodesCopy =
          setParent(newlyCheckedNode.parentId, checkedNodesCopy.slice()) ??
          checkedNodesCopy; // check the parent and its parents until we reach the root
      } else {
        checkedNodesCopy = checkedNodesCopy.slice(); // set a new instance to the checked list
      }

      // this sets only the checked nodes to the form controller value
      onChange(
        checkedNodesCopy?.filter(
          (el) => el.checked === TristateStateEnum.CHECKED
        )
      );

      setCheckedNodes(checkedNodesCopy);
    },
    [treeData]
  );

  const basePlaceholder = placeholder || "Type to search...";
  const searchPlaceholder = isLargeTree
    ? `${basePlaceholder} ${MIN_SEARCH_LENGTH_HINT}`
    : basePlaceholder;

  const classNameWrapper = ClassNames("tree-wrapper", "input-wrap", className);

  return (
    <>
      {isSearchable && (
        <div className="tree-nodes-search">
          {label && (
            <label className={`input-label`}>
              {label}{" "}
              {!required && !hideLabelHelper && (
                <span className={"label-helper"}>Optional</span>
              )}
            </label>
          )}
          <TextInput
            data-testid={"tree-search"}
            value={searchValue}
            placeholder={searchPlaceholder}
            isClearable
            leftIcon={"seem-icon-search"}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const value = e.target.value || "";
              setSearchValue(value);
              setIsSearching(isSearchTermActive(value, minSearchLength));
            }}
            rightIcon={isSearching ? "seem-icon-spinner spinner" : ""}
            applyAutoFocus
            className={"no-padding-bottom"}
          />
        </div>
      )}
      <div
        className={`tree-nodes-container ${isSearchable ? "with-search" : ""}`}
      >
        <div
          className={`${classNameWrapper} ${
            borderlessNodes ? "borderless-nodes" : ""
          } ${withCheckbox ? "has-checkbox" : ""}
         ${previewMode ? "preview-mode" : ""}
         ${isStatic ? "is-static" : ""}
         `}
          data-testid={dataTestId}
        >
          {!!treeData && treeData.length ? (
            <div
              className={"tree-nodes-wrap"}
              data-testid={"tree-nodes-wrapper"}
            >
              {treeData.map((node) => {
                return (
                  <TreeNode
                    {...node}
                    checked={"false"}
                    nodeData={node}
                    parentId={node.parentId}
                    level={0}
                    nodeOperations={{
                      ...nodeOperations,
                      onCheck: handleNodeCheck,
                    }}
                    searchValue={effectiveSearchValue}
                    data-testid={`tree-node-${node.name}`}
                    key={`tree-node-${node.name}`}
                    editableNodes={editableItems}
                    selectedNode={selectedNode ?? undefined}
                    isLastChild={true}
                    withCheckbox={withCheckbox}
                    checkedNodes={checkedNodes}
                    initialExpandLevel={initialExpandLevel}
                    expandedNodes={expandedNodes}
                    isSelectableNode={isSelectableNode}
                    isHiddenRoot={isHiddenRoot}
                    readOnly={readOnly}
                    warningTooltip={warningTooltip}
                    getActionTooltips={getActionTooltips}
                    scrollToSelected={scrollToSelected}
                    getNodeIconByType={getNodeIconByType}
                    getNodeActions={getNodeActions}
                    previewMode={previewMode}
                    getRightComponent={getRightComponent}
                    getExtendedTitle={getExtendedTitle}
                    tooltipContent={tooltipContent}
                    isStatic={isStatic}
                    isHighlightedNode={isHighlightedNode}
                  />
                );
              })}
            </div>
          ) : !!treeData && !treeData.length ? (
            noDataState
          ) : (
            loadingState
          )}
        </div>
      </div>
      {error && (
        <FormFieldError errorMessage={error.message} errorType={error.type} />
      )}
    </>
  );
};

export default Tree;
